/**
 * effectsDisplayConfig.js
 *
 * Configuration d'affichage des effets.
 *
 * Le champ `symbol` accepte deux formats :
 *   - Un emoji ou texte court :  "☀"
 *   - Un chemin vers un asset public : "/assets/icons/timing-jour.png"
 *     (tout ce qui commence par "/" ou "http" est traité comme une image)
 *
 * La valeur `internalValue` (clé) est toujours ce qui est envoyé au back,
 * sans modification.
 *
 * ── Exemples ────────────────────────────────────────────────────────────────
 *
 *   "{J}": { label: "Jour",   symbol: "/assets/icons/timing-jour.svg"  }
 *   "{V}": { label: "Forêt",  symbol: "/assets/icons/terrain-foret.png" }
 *   "{1}": { label: "1 Mana", symbol: "①" }   ← emoji, pas d'image
 *
 * ── Chemins recommandés ─────────────────────────────────────────────────────
 *
 *   Placez vos icônes dans :  public/assets/icons/
 *   Référencez-les comme :    "/assets/icons/mon-icone.png"
 *   (le "/" initial pointe vers la racine de public/)
 */

export const EFFECT_DISPLAY_MAP = {
    // ── Timings ────────────────────────────────────────────────────────────────
    // Remplacez le champ `symbol` par un chemin d'image si vous avez l'asset,
    // ex: "{J}": { label: "Jour", symbol: "/assets/icons/timing-jour.svg" }
    "{J}": { label: "",       symbol: "/assets/fleche.svg"  },
    "{H}": { label: "",      symbol: "/assets/hand.svg" },
    "{D}": { label: "", symbol: "/assets/support.svg" },
    "{R}": { label: "",      symbol: "/assets/reserve.svg" },
    "{I}": { label: "", symbol: "/assets/infinite.svg" },
    // Obligé de rajouter l'espace car raw_text renvoyé par Altered a un espace pour ce champ précis
    "[]":  { label: "",      symbol: "[]", queryValue: " []" },

    // ── Terrains ───────────────────────────────────────────────────────────────
    // Exemples avec images (décommentez et adaptez selon vos assets) :
    // "{V}": { label: "Forêt",    symbol: "/assets/icons/terrain-foret.png"    },
    // "{M}": { label: "Montagne", symbol: "/assets/icons/terrain-montagne.png" },
    // "{O}": { label: "Océan",    symbol: "/assets/icons/terrain-ocean.png"    },
    "{V}": { label: "",    symbol: "/assets/forest.svg" },
    "{M}": { label: "", symbol: "/assets/mountain.svg"  },
    "{O}": { label: "",    symbol: "/assets/water.svg" },

    // ── Coûts / Mana ───────────────────────────────────────────────────────────
    "{1}": { label: "1 Mana",  symbol: "①" },
    "{2}": { label: "2 Mana",  symbol: "②" },
    "{3}": { label: "3 Mana",  symbol: "③" },
    "{4}": { label: "4 Mana",  symbol: "④" },
    "{T}": { label: "", symbol: "{T}" },
};

/**
 * Retourne la valeur à envoyer au back pour un code donné.
 * Si `queryValue` est défini, retourne queryValue. Sinon retourne le code lui-même.
 *
 * @param {string} code - clé du EFFECT_DISPLAY_MAP (ex: "{J}", "[]")
 * @returns {string}
 */
export function getQueryValue(code) {
    const entry = EFFECT_DISPLAY_MAP[code];
    if (!entry) return code;
    return entry.queryValue ?? code;
}

/**
 * Indique si un symbole est un chemin image (commence par "/" ou "http").
 */
function isImageSymbol(symbol) {
    return !!symbol && (symbol.startsWith("/") || symbol.startsWith("http"));
}

/**
 * Retourne le symbole associé à un code interne.
 * Utile pour les composants qui gèrent eux-mêmes le rendu image/texte.
 *
 * @param {string} internalValue
 * @returns {string|undefined}
 */
export function getSymbol(internalValue) {
    return EFFECT_DISPLAY_MAP[internalValue]?.symbol;
}

/**
 * Retourne le libellé texte d'un code interne.
 * Si aucune correspondance, retourne la valeur brute.
 * Pour les symboles image, retourne uniquement le label (pas de JSX ici).
 *
 * @param {string} internalValue
 * @param {boolean} withSymbol - préfixer avec le symbole emoji (ignoré pour les images)
 * @returns {string}
 */
export function getDisplayLabel(internalValue, withSymbol = true) {
    const entry = EFFECT_DISPLAY_MAP[internalValue];
    if (!entry) return internalValue;
    if (!withSymbol || !entry.symbol || isImageSymbol(entry.symbol)) return entry.label;
    return `${entry.symbol} ${entry.label}`;
}

/**
 * Applique les substitutions TEXTE dans une chaîne.
 * Les symboles image sont remplacés par leur label seul (pas de JSX ici).
 * Pour un rendu avec les images, utilisez renderOptionLabel() dans le composant React.
 *
 * @param {string} text
 * @returns {string}
 */
export function applyDisplaySubstitutions(text) {
    if (!text) return text;
    let result = text;
    // Trie par longueur décroissante pour éviter les remplacements partiels
    const entries = Object.entries(EFFECT_DISPLAY_MAP).sort(
        (a, b) => b[0].length - a[0].length
    );
    entries.forEach(([code, { symbol, label }]) => {
        const displayText = symbol && !isImageSymbol(symbol)
            ? `${symbol} ${label}`
            : label;
        const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        result = result.replace(new RegExp(escaped, "g"), displayText);
    });
    return result;
}